import type { MasteryLevel } from "./tokens";

// Approved terminology exception: this legacy admin-analytics mock dataset
// models tutoring support levels, unrelated to lesson sequencing.
export const TOTAL_STANDARDS = 81;
// Standards vary in size, so mastery is computed from the finer-grained
// learning components (skills) beneath them. The standards view is kept for browsing.
export const TOTAL_COMPONENTS = 341;

// Grade-level totals (skills sum to TOTAL_COMPONENTS; standards to TOTAL_STANDARDS)
export const GRADE_SKILLS: Record<number, number> = { 6: 122, 7: 118, 8: 101 };
export const GRADE_STANDARDS: Record<number, number> = { 6: 29, 7: 28, 8: 24 };

// A standard contains ~4.2 skills on average.
export const SKILL_FACTOR = TOTAL_COMPONENTS / TOTAL_STANDARDS;

// Minimum students in a selection before per-student distributions are reliable.
export const MIN_STUDENTS = 15;

export type Unit = "skills" | "standards";

// Convert a standards-based count into the active unit (skills or standards).
export function toUnit(standardsValue: number, unit: Unit): number {
  return unit === "skills"
    ? Math.round(standardsValue * SKILL_FACTOR)
    : Math.round(standardsValue);
}
export function unitTotal(unit: Unit): number {
  return unit === "skills" ? TOTAL_COMPONENTS : TOTAL_STANDARDS;
}
// grade-level total in the active unit (never use the all-grades total for one grade/person)
export function gradeUnitTotal(grade: number, unit: Unit): number {
  return unit === "skills"
    ? (GRADE_SKILLS[grade] ?? 0)
    : (GRADE_STANDARDS[grade] ?? 0);
}
// average grade-level total across the selected grades (for per-student aggregates)
export function avgGradeUnitTotal(grades: number[], unit: Unit): number {
  const gs = grades.length ? grades : [6, 7, 8];
  const sum = gs.reduce((s, g) => s + gradeUnitTotal(g, unit), 0);
  return Math.round(sum / gs.length);
}
export function unitNoun(unit: Unit, plural = true): string {
  const base = unit === "skills" ? "skill" : "standard";
  return plural ? `${base}s` : base;
}
export function unitNounCap(unit: Unit): string {
  return unit === "skills" ? "Skills" : "Standards";
}
// Integer percentages that always sum to 100 (largest-remainder method).
export function distributePercents(values: number[]): number[] {
  const total = values.reduce((s, v) => s + v, 0);
  if (total <= 0) return values.map(() => 0);
  const raw = values.map((v) => (v / total) * 100);
  const floor = raw.map((r) => Math.floor(r));
  let remainder = 100 - floor.reduce((s, v) => s + v, 0);
  const order = raw
    .map((r, i) => ({ i, frac: r - Math.floor(r) }))
    .sort((a, b) => b.frac - a.frac);
  const out = [...floor];
  for (let k = 0; k < order.length && remainder > 0; k++) {
    out[order[k].i] += 1;
    remainder--;
  }
  return out;
}
// Distribute an integer `target` across positive `weights` so the returned
// integers always sum to exactly `target` (largest-remainder method). Used to
// keep the skills column reconciled to grade-level totals per domain.
export function largestRemainder(target: number, weights: number[]): number[] {
  const totalW = weights.reduce((s, w) => s + Math.max(0, w), 0);
  if (target <= 0 || totalW <= 0) return weights.map(() => 0);
  const raw = weights.map((w) => (Math.max(0, w) / totalW) * target);
  const floor = raw.map((r) => Math.floor(r));
  let remainder = target - floor.reduce((s, v) => s + v, 0);
  const order = raw
    .map((r, i) => ({ i, frac: r - Math.floor(r) }))
    .sort((a, b) => b.frac - a.frac);
  const out = [...floor];
  for (let k = 0; k < order.length && remainder > 0; k++) {
    out[order[k].i] += 1;
    remainder--;
  }
  return out;
}
// School days in the selected period, for section captions.
export const SCHOOL_DAYS: Record<string, number> = {
  "This week": 5,
  "This month": 21,
  "This semester": 88,
  "This year": 142,
};
// x-axis buckets for the "mastered per student" bar chart, in the active unit
export const HISTOGRAM_BUCKETS_STANDARDS = [
  "0-5",
  "6-10",
  "11-15",
  "16-20",
  "21-25",
  "26+",
];
export const HISTOGRAM_BUCKETS_SKILLS = [
  "0-20",
  "21-40",
  "41-60",
  "61-80",
  "81-100",
  "100+",
];
export function histogramBuckets(unit: Unit): string[] {
  return unit === "skills"
    ? HISTOGRAM_BUCKETS_SKILLS
    : HISTOGRAM_BUCKETS_STANDARDS;
}

// Headline metrics use a FIXED 30-day window and are intentionally NOT affected
// by the filter row. On Track to Proficiency refreshes monthly.
export const HEADLINE = {
  // Active students (last 30 days) as a rate of lifetime users
  activeRate: 72, // %
  activeCount: 924,
  lifetimeStudents: 1284,
  activeTrend: 3, // % vs prior 30 days

  // Average time per student (last 30 days)
  avgHours: 6.2,
  lifetimeHours: 52140, // all-time hours across every student
  avgHoursTrend: 8, // % vs prior 30 days

  // On track to grade-level proficiency by year end (component-based, monthly)
  onTrackPct: 64, // %
  onTrackTrend: 2, // % vs last month
  acceleration: 128, // components mastered vs expected pace (%)
} as const;

export const SCHOOL_NAMES = [
  "Greater Dayton School",
  "Riverside Middle",
  "Oakwood Charter",
  "St. Anne's Academy",
] as const;

export const TEACHERS_BY_SCHOOL: Record<string, string[]> = {
  "Greater Dayton School": ["Katie Andorka", "Linda J.", "Marcus Bell"],
  "Riverside Middle": ["Dana Whitfield", "Marcus Bell"],
  "Oakwood Charter": ["Katie Andorka", "Linda J."],
  "St. Anne's Academy": ["Dana Whitfield", "Linda J."],
};

export const STUDENT_GROUPS = [
  "Tier 2 Intervention",
  "Tier 3 Intervention",
  "Tier 1 Enrichment",
  "Afterschool Cohort A",
  "Bubble Students",
] as const;

// Admin-curated student tags. Empty selection = "All categories".
export const CATEGORIES = ["Tier 1", "Tier 2", "Tier 3", "SPED"] as const;

// Named Common Core standards with the share of students who have mastered each,
// used for the "most / least mastered" view.
export interface StandardMastery {
  code: string;
  name: string;
  grade: number;
  masteredPct: number;
}
export const STANDARD_MASTERY: StandardMastery[] = [
  {
    code: "6.RP.A.1",
    name: "Understand ratio concepts",
    grade: 6,
    masteredPct: 82,
  },
  {
    code: "6.G.A.1",
    name: "Area of triangles & polygons",
    grade: 6,
    masteredPct: 79,
  },
  {
    code: "6.NS.C.6",
    name: "Rational numbers on the number line",
    grade: 6,
    masteredPct: 74,
  },
  {
    code: "6.SP.B.5",
    name: "Summarize numerical data",
    grade: 6,
    masteredPct: 63,
  },
  {
    code: "6.EE.B.5",
    name: "Solve one-variable equations",
    grade: 6,
    masteredPct: 58,
  },
  {
    code: "7.RP.A.2",
    name: "Recognize proportional relationships",
    grade: 7,
    masteredPct: 68,
  },
  {
    code: "7.NS.A.1",
    name: "Add & subtract rational numbers",
    grade: 7,
    masteredPct: 57,
  },
  {
    code: "7.EE.B.4",
    name: "Solve equations from word problems",
    grade: 7,
    masteredPct: 44,
  },
  {
    code: "7.G.B.6",
    name: "Area, volume & surface area",
    grade: 7,
    masteredPct: 41,
  },
  {
    code: "8.EE.C.7",
    name: "Solve linear equations",
    grade: 8,
    masteredPct: 71,
  },
  { code: "8.G.A.5", name: "Angle relationships", grade: 8, masteredPct: 66 },
  {
    code: "8.SP.A.1",
    name: "Interpret scatter plots",
    grade: 8,
    masteredPct: 48,
  },
  { code: "8.F.A.1", name: "Understand functions", grade: 8, masteredPct: 39 },
  {
    code: "8.NS.A.1",
    name: "Rational vs. irrational numbers",
    grade: 8,
    masteredPct: 36,
  },
];

// Contracted tutoring hours for the term (drives the "what you paid for" row).
export const PURCHASED_HOURS = 10000;

// ---- Adoption / engagement sub-metrics -------------------------------------
// District active-students target used for gap-to-benchmark math.
export const ACTIVE_BENCHMARK = 70;

export interface ActiveRow {
  name: string;
  active: number;
  total: number;
}
export const ACTIVE_BY_SITE: ActiveRow[] = [
  { name: "Greater Dayton School", active: 380, total: 512 },
  { name: "Riverside Middle", active: 268, total: 388 },
  { name: "Oakwood Charter", active: 178, total: 240 },
  { name: "St. Anne's Academy", active: 98, total: 144 },
];
export const ACTIVE_BY_GRADE: ActiveRow[] = [
  { name: "Grade 6", active: 355, total: 475 },
  { name: "Grade 7", active: 315, total: 436 },
  { name: "Grade 8", active: 254, total: 373 },
];
export const ACTIVE_BY_CATEGORY: ActiveRow[] = [
  { name: "Tier 1", active: 470, total: 560 },
  { name: "Tier 2", active: 210, total: 320 },
  { name: "Tier 3", active: 88, total: 150 },
  { name: "SPED", active: 96, total: 140 },
];

// Weekly active-% trend (8 points, oldest -> now).
export const TREND_WEEKS = ["7w", "6w", "5w", "4w", "3w", "2w", "1w", "Now"];
export const DISTRICT_ACTIVE_TREND = [72, 73, 73, 74, 74, 73, 73, 72];
export interface SiteTrend {
  name: string;
  series: number[];
}
export const SITE_ACTIVE_TRENDS: SiteTrend[] = [
  { name: "Greater Dayton School", series: [72, 72, 73, 73, 74, 74, 74, 74] },
  { name: "Riverside Middle", series: [79, 78, 77, 76, 75, 74, 73, 72] },
  { name: "Oakwood Charter", series: [70, 71, 72, 73, 74, 74, 74, 74] },
  { name: "St. Anne's Academy", series: [71, 70, 70, 69, 69, 68, 68, 68] },
];

// Share of students in each hours-per-student band (last 30 days). Sums to 1.
export const HOURS_BANDS: { band: string; share: number; under: boolean }[] = [
  { band: "0–1", share: 0.04, under: true },
  { band: "1–2", share: 0.06, under: true },
  { band: "2–3", share: 0.08, under: true },
  { band: "3–4", share: 0.1, under: true },
  { band: "4–5", share: 0.14, under: false },
  { band: "5–6", share: 0.16, under: false },
  { band: "6–7", share: 0.14, under: false },
  { band: "7–8", share: 0.11, under: false },
  { band: "8–9", share: 0.08, under: false },
  { band: "9–10", share: 0.05, under: false },
  { band: "10+", share: 0.04, under: false },
];

// Share of students active N consecutive weeks.
export const STREAK_SHARES = { w2: 0.62, w4: 0.41, w6: 0.24 };

export const GRADES = [6, 7, 8] as const;

export const DATE_RANGES = [
  "This week",
  "This month",
  "This semester",
  "This year",
] as const;

// Structural Common Core domain sizes per grade (sum to grade totals 29 / 28 / 24)
export const DOMAIN_TOTALS: Record<
  number,
  { RP: number; NS: number; EE: number; G: number; SP: number }
> = {
  6: { RP: 6, NS: 8, EE: 7, G: 4, SP: 4 },
  7: { RP: 5, NS: 6, EE: 8, G: 5, SP: 4 },
  8: { RP: 2, NS: 3, EE: 9, G: 6, SP: 4 },
};

export const DOMAIN_LABELS: Record<string, string> = {
  RP: "Ratios & Proportional Relationships",
  NS: "The Number System",
  EE: "Expressions & Equations",
  G: "Geometry",
  SP: "Statistics & Probability",
};

export interface GradeStack {
  grade: number;
  total: number;
  notStarted: number;
  approaching: number;
  proficient: number;
  mastered: number;
}

export interface SchoolRow {
  name: string;
  students: number;
  medianMastered: number; // n / 81
  avgMastery: number; // percent
  trend: number[]; // last 6 months
  mom: number; // month-over-month pts
  medianLessons: number; // for Modal A
}

export interface Variant {
  key: string;
  kpi: {
    standardsMastered: number;
    standardsMasteredDelta: string;
    avgMastery: number;
    avgMasteryDelta: string;
  };
  histogram: {
    buckets: number[];
    medianIndex: number;
    medianLessons: number;
    total: number;
  };
  grades: GradeStack[];
  engagement: {
    activePct: number;
    activeSub: string;
    activeMoM: number | null;
    activeCount: number;
    standardsPracticed: number;
    tutoringHours: string;
    tutoringHoursNum: number;
    tutoringSub: string;
    tutoringPerStudentNum: number;
    sessions: string;
    sessionsNum: number;
    sessionsSub: string;
  };
  lessons: {
    assigned: number;
    assignedSub: string;
    completed: number;
    completedSub: string;
    completionRate: number;
    completionMoM: number;
    tutorHours: string;
    tutorHoursNum: number;
    minPerLesson: number;
    tutorHoursSub: string;
  };
  scaffolding: {
    total: number;
    totalSub: string;
    breakdown: { label: string; value: number; color: string }[];
  };
  teachers: {
    name: string;
    site: string; // primary school/site
    grade: number;
    lessonsAssigned: number;
    sessionsReviewed: number;
    studentsAssigned: number;
    activeStudents: number; // active out of studentsAssigned (last 30 days)
    avgHours: number; // avg tutoring hours per student
    onTrackPct: number; // % of their students on track to year-end proficiency
    onTrackDelta: number; // pts vs last month
    avgMastery: number; // percent of grade-level skills/standards mastered by their students
    activity: number;
    lastActive: string;
  }[];
  masteryGain: {
    pts: number;
    sub: string;
    trend: number[];
  };
  // average standards mastery per month, Aug through Jun (11 points)
  masteryOverTime: number[];
  // distribution across the four mastery levels (values are counts)
  standardPerf: { key: MasteryLevel; value: number }[];
  studentPerf: { key: MasteryLevel; value: number }[];
  // student spotlight, ranked by a normalized mastery score (bite quality, 0-5)
  topStudents: Student[];
  bottomStudents: Student[];
  actionable: Actionable;
  schools: SchoolRow[];
  allTrend: number[];
  allMom: number;
  groupsMedianLessons: { name: string; value: number }[];
}

// A spotlight student. pct = % of their grade-level skills mastered;
// score = average bite quality (Flawless 5 / medium 3 / low 2 / high 1); bites = data volume.
export interface Student {
  name: string;
  grade: number;
  pct: number;
  score: number;
  bites: number;
  site?: string;
  teacher?: string;
  category?: string;
}

// A student surfaced on an action worklist, with the context that makes them actionable.
export interface RosterStudent {
  name: string;
  grade: number;
  site: string;
  teacher: string;
  detail: string;
}

// Forward-looking, decision-driving figures for the proficiency section.
export interface Actionable {
  targetPct: number; // year-end on-track goal
  projectedPct: number; // projected year-end on-track at current pace
  expectedStandards: number; // standards the median student should have mastered by now (pace)
  weeksRemaining: number; // instructional weeks left this year
  catchUpSkillsPerWeek: number; // skills/week per off-track student needed to recover
  offTrackShare: number; // share of students NOT on track
  bubbleShare: number; // within a few skills of the next band
  stalledShare: number; // active but no progress in 14+ days
  slippingShare: number; // was on track last month, now off
  bubbleRoster: RosterStudent[];
  stalledRoster: RosterStudent[];
  slippingRoster: RosterStudent[];
}

export const HISTOGRAM_BUCKETS = [
  "0-5",
  "6-10",
  "11-15",
  "16-20",
  "21-25",
  "26+",
];

const DEFAULT_VARIANT: Variant = {
  key: "default",
  kpi: {
    standardsMastered: 34,
    standardsMasteredDelta: "+6 this month",
    avgMastery: 58,
    avgMasteryDelta: "+4 pts vs last month",
  },
  histogram: {
    buckets: [120, 250, 300, 290, 200, 124],
    medianIndex: 2,
    medianLessons: 14,
    total: 1284,
  },
  grades: [
    {
      grade: 6,
      total: 29,
      notStarted: 3,
      approaching: 6,
      proficient: 8,
      mastered: 12,
    },
    {
      grade: 7,
      total: 28,
      notStarted: 3,
      approaching: 6,
      proficient: 7,
      mastered: 12,
    },
    {
      grade: 8,
      total: 24,
      notStarted: 3,
      approaching: 5,
      proficient: 6,
      mastered: 10,
    },
  ],
  engagement: {
    activePct: 72,
    activeSub: "924 of 1,284 this period",
    activeMoM: null,
    activeCount: 924,
    standardsPracticed: 63,
    tutoringHours: "6,900 hrs",
    tutoringHoursNum: 6900,
    tutoringSub: "5.4 hrs avg per student",
    tutoringPerStudentNum: 5.4,
    sessions: "11.2",
    sessionsNum: 11.2,
    sessionsSub: "median, this school year",
  },
  lessons: {
    assigned: 18420,
    assignedSub: "across 81 standards",
    completed: 14268,
    completedSub: "77% of assigned lessons",
    completionRate: 77,
    completionMoM: 3,
    tutorHours: "5,800 hrs",
    tutorHoursNum: 5800,
    minPerLesson: 24,
    tutorHoursSub: "24 min avg per lesson",
  },
  scaffolding: {
    total: 46500,
    totalSub: "across 14,268 completed lessons",
    breakdown: [
      {
        label: "Flawless lessons",
        value: 20300,
        color: "var(--scaffold-flawless)",
      },
      { label: "Low scaffolding", value: 11900, color: "var(--scaffold-low)" },
      {
        label: "Medium scaffolding",
        value: 6600,
        color: "var(--scaffold-medium)",
      },
      { label: "High scaffolding", value: 3500, color: "var(--scaffold-high)" },
      {
        label: "Incomplete lessons",
        value: 4200,
        color: "var(--scaffold-incomplete)",
      },
    ],
  },
  teachers: [
    {
      name: "Katie Andorka",
      site: "Greater Dayton School",
      grade: 6,
      lessonsAssigned: 4820,
      sessionsReviewed: 312,
      studentsAssigned: 168,
      activeStudents: 138,
      avgHours: 7.1,
      onTrackPct: 71,
      onTrackDelta: 4,
      avgMastery: 64,
      activity: 92,
      lastActive: "2h ago",
    },
    {
      name: "Dana Whitfield",
      site: "Riverside Middle",
      grade: 7,
      lessonsAssigned: 3910,
      sessionsReviewed: 248,
      studentsAssigned: 142,
      activeStudents: 101,
      avgHours: 6.0,
      onTrackPct: 62,
      onTrackDelta: 2,
      avgMastery: 59,
      activity: 78,
      lastActive: "Today",
    },
    {
      name: "Marcus Bell",
      site: "Greater Dayton School",
      grade: 8,
      lessonsAssigned: 3540,
      sessionsReviewed: 205,
      studentsAssigned: 121,
      activeStudents: 82,
      avgHours: 5.4,
      onTrackPct: 57,
      onTrackDelta: -1,
      avgMastery: 56,
      activity: 70,
      lastActive: "Yesterday",
    },
    {
      name: "Linda J.",
      site: "Oakwood Charter",
      grade: 7,
      lessonsAssigned: 2760,
      sessionsReviewed: 156,
      studentsAssigned: 98,
      activeStudents: 61,
      avgHours: 4.6,
      onTrackPct: 51,
      onTrackDelta: 1,
      avgMastery: 52,
      activity: 54,
      lastActive: "3d ago",
    },
  ],
  masteryGain: {
    pts: 7,
    sub: "more mastered, this school year",
    trend: [51, 53, 54, 56, 57, 58],
  },
  masteryOverTime: [47, 49, 51, 52, 54, 55, 56, 57, 57, 58, 58],
  standardPerf: [
    { key: "notStarted", value: 8 },
    { key: "approaching", value: 18 },
    { key: "proficient", value: 25 },
    { key: "mastered", value: 30 },
  ],
  studentPerf: [
    { key: "notStarted", value: 96 },
    { key: "approaching", value: 268 },
    { key: "proficient", value: 561 },
    { key: "mastered", value: 359 },
  ],
  topStudents: [
    {
      name: "Aisha K.",
      grade: 6,
      pct: 96,
      score: 4.8,
      bites: 210,
      site: "Greater Dayton School",
      teacher: "Katie Andorka",
      category: "Tier 1",
    },
    {
      name: "Diego M.",
      grade: 7,
      pct: 95,
      score: 4.7,
      bites: 240,
      site: "Riverside Middle",
      teacher: "Dana Whitfield",
      category: "Tier 1",
    },
    {
      name: "Priya S.",
      grade: 8,
      pct: 93,
      score: 4.7,
      bites: 190,
      site: "Greater Dayton School",
      teacher: "Marcus Bell",
      category: "Tier 2",
    },
    {
      name: "Grace T.",
      grade: 7,
      pct: 91,
      score: 4.5,
      bites: 205,
      site: "Oakwood Charter",
      teacher: "Linda J.",
      category: "Tier 1",
    },
    {
      name: "Liam O.",
      grade: 6,
      pct: 90,
      score: 4.6,
      bites: 175,
      site: "Greater Dayton School",
      teacher: "Katie Andorka",
      category: "SPED",
    },
    {
      name: "Hana W.",
      grade: 8,
      pct: 89,
      score: 4.5,
      bites: 198,
      site: "Riverside Middle",
      teacher: "Marcus Bell",
      category: "Tier 1",
    },
    {
      name: "Noah D.",
      grade: 7,
      pct: 88,
      score: 4.4,
      bites: 182,
      site: "Oakwood Charter",
      teacher: "Linda J.",
      category: "Tier 2",
    },
    {
      name: "Sofia L.",
      grade: 6,
      pct: 87,
      score: 4.4,
      bites: 168,
      site: "St. Anne's Academy",
      teacher: "Dana Whitfield",
      category: "Tier 1",
    },
    {
      name: "Mateo R.",
      grade: 8,
      pct: 86,
      score: 4.3,
      bites: 176,
      site: "Greater Dayton School",
      teacher: "Marcus Bell",
      category: "Tier 3",
    },
    {
      name: "Emma J.",
      grade: 7,
      pct: 85,
      score: 4.3,
      bites: 160,
      site: "Riverside Middle",
      teacher: "Dana Whitfield",
      category: "Tier 1",
    },
  ],
  bottomStudents: [
    { name: "Noah R.", grade: 6, pct: 12, score: 1.6, bites: 62 },
    { name: "Ethan B.", grade: 7, pct: 15, score: 1.8, bites: 74 },
    { name: "Maya L.", grade: 8, pct: 15, score: 1.9, bites: 55 },
    { name: "Owen C.", grade: 6, pct: 18, score: 2.0, bites: 80 },
    { name: "Zoe P.", grade: 7, pct: 22, score: 2.1, bites: 66 },
  ],
  actionable: {
    targetPct: 75,
    projectedPct: 68,
    expectedStandards: 16,
    weeksRemaining: 11,
    catchUpSkillsPerWeek: 2,
    offTrackShare: 0.36,
    bubbleShare: 0.143,
    stalledShare: 0.055,
    slippingShare: 0.047,
    bubbleRoster: [
      {
        name: "Jordan P.",
        grade: 6,
        site: "Riverside Middle",
        teacher: "Dana Whitfield",
        detail: "2 skills from Proficient",
      },
      {
        name: "Maya L.",
        grade: 7,
        site: "Greater Dayton School",
        teacher: "Katie Andorka",
        detail: "1 skill from Proficient",
      },
      {
        name: "Owen C.",
        grade: 8,
        site: "Greater Dayton School",
        teacher: "Marcus Bell",
        detail: "3 skills from Mastered",
      },
      {
        name: "Zoe R.",
        grade: 6,
        site: "Oakwood Charter",
        teacher: "Linda J.",
        detail: "2 skills from Proficient",
      },
      {
        name: "Eli M.",
        grade: 7,
        site: "Riverside Middle",
        teacher: "Dana Whitfield",
        detail: "4 skills from Proficient",
      },
      {
        name: "Ava T.",
        grade: 8,
        site: "Oakwood Charter",
        teacher: "Linda J.",
        detail: "1 skill from Mastered",
      },
      {
        name: "Caleb W.",
        grade: 6,
        site: "Greater Dayton School",
        teacher: "Katie Andorka",
        detail: "3 skills from Proficient",
      },
      {
        name: "Nina F.",
        grade: 7,
        site: "St. Anne's Academy",
        teacher: "Dana Whitfield",
        detail: "2 skills from Proficient",
      },
    ],
    stalledRoster: [
      {
        name: "Noah R.",
        grade: 6,
        site: "Riverside Middle",
        teacher: "Dana Whitfield",
        detail: "0 skills in 18 days · 6.1 hrs logged",
      },
      {
        name: "Ethan B.",
        grade: 7,
        site: "Greater Dayton School",
        teacher: "Katie Andorka",
        detail: "0 skills in 21 days · 5.4 hrs logged",
      },
      {
        name: "Priya K.",
        grade: 8,
        site: "Greater Dayton School",
        teacher: "Marcus Bell",
        detail: "0 skills in 16 days · 7.0 hrs logged",
      },
      {
        name: "Sam D.",
        grade: 6,
        site: "Oakwood Charter",
        teacher: "Linda J.",
        detail: "0 skills in 24 days · 4.8 hrs logged",
      },
      {
        name: "Lena F.",
        grade: 7,
        site: "Riverside Middle",
        teacher: "Dana Whitfield",
        detail: "0 skills in 15 days · 5.9 hrs logged",
      },
      {
        name: "Kofi A.",
        grade: 8,
        site: "Oakwood Charter",
        teacher: "Linda J.",
        detail: "0 skills in 19 days · 6.3 hrs logged",
      },
    ],
    slippingRoster: [
      {
        name: "Grace H.",
        grade: 6,
        site: "Greater Dayton School",
        teacher: "Katie Andorka",
        detail: "Fell below pace 2 weeks ago · -6%",
      },
      {
        name: "Leo N.",
        grade: 7,
        site: "Riverside Middle",
        teacher: "Dana Whitfield",
        detail: "On track last month · -8%",
      },
      {
        name: "Mia Q.",
        grade: 8,
        site: "Greater Dayton School",
        teacher: "Marcus Bell",
        detail: "On track last month · -5%",
      },
      {
        name: "Diego F.",
        grade: 6,
        site: "Oakwood Charter",
        teacher: "Linda J.",
        detail: "Fell below pace 3 weeks ago · -7%",
      },
      {
        name: "Hana S.",
        grade: 7,
        site: "St. Anne's Academy",
        teacher: "Dana Whitfield",
        detail: "On track last month · -4%",
      },
    ],
  },
  schools: [
    {
      name: "Greater Dayton School",
      students: 512,
      medianMastered: 37,
      avgMastery: 62,
      trend: [55, 57, 58, 60, 61, 62],
      mom: 3,
      medianLessons: 15,
    },
    {
      name: "Riverside Middle",
      students: 388,
      medianMastered: 30,
      avgMastery: 54,
      trend: [56, 57, 56, 57, 56, 54],
      mom: -2,
      medianLessons: 12,
    },
    {
      name: "Oakwood Charter",
      students: 240,
      medianMastered: 39,
      avgMastery: 66,
      trend: [58, 60, 61, 63, 64, 66],
      mom: 5,
      medianLessons: 17,
    },
    {
      name: "St. Anne's Academy",
      students: 144,
      medianMastered: 33,
      avgMastery: 57,
      trend: [52, 53, 54, 55, 56, 57],
      mom: 2,
      medianLessons: 13,
    },
  ],
  allTrend: [51, 53, 54, 56, 57, 58],
  allMom: 4,
  groupsMedianLessons: [
    { name: "Tier 2 Intervention", value: 9 },
    { name: "Tier 3 Intervention", value: 7 },
    { name: "Tier 1 Enrichment", value: 22 },
    { name: "Afterschool Cohort A", value: 13 },
    { name: "Bubble Students", value: 11 },
  ],
};

const TIER2_VARIANT: Variant = {
  key: "tier2",
  kpi: {
    standardsMastered: 21,
    standardsMasteredDelta: "+3 this month",
    avgMastery: 41,
    avgMasteryDelta: "+2 pts vs last month",
  },
  histogram: {
    buckets: [78, 120, 84, 40, 15, 5],
    medianIndex: 1,
    medianLessons: 9,
    total: 342,
  },
  grades: [
    {
      grade: 6,
      total: 29,
      notStarted: 6,
      approaching: 8,
      proficient: 7,
      mastered: 8,
    },
    {
      grade: 7,
      total: 28,
      notStarted: 6,
      approaching: 8,
      proficient: 7,
      mastered: 7,
    },
    {
      grade: 8,
      total: 24,
      notStarted: 6,
      approaching: 7,
      proficient: 5,
      mastered: 6,
    },
  ],
  engagement: {
    activePct: 68,
    activeSub: "233 of 342 this period",
    activeMoM: -5,
    activeCount: 233,
    standardsPracticed: 58,
    tutoringHours: "2,050 hrs",
    tutoringHoursNum: 2050,
    tutoringSub: "6.0 hrs avg per student",
    tutoringPerStudentNum: 6.0,
    sessions: "9.4",
    sessionsNum: 9.4,
    sessionsSub: "median, this school year",
  },
  lessons: {
    assigned: 4120,
    assignedSub: "across 81 standards",
    completed: 2690,
    completedSub: "65% of assigned lessons",
    completionRate: 65,
    completionMoM: -2,
    tutorHours: "1,170 hrs",
    tutorHoursNum: 1170,
    minPerLesson: 26,
    tutorHoursSub: "26 min avg per lesson",
  },
  scaffolding: {
    total: 14000,
    totalSub: "across 2,690 completed lessons",
    breakdown: [
      {
        label: "Flawless lessons",
        value: 2700,
        color: "var(--scaffold-flawless)",
      },
      { label: "Low scaffolding", value: 3200, color: "var(--scaffold-low)" },
      {
        label: "Medium scaffolding",
        value: 3000,
        color: "var(--scaffold-medium)",
      },
      { label: "High scaffolding", value: 2500, color: "var(--scaffold-high)" },
      {
        label: "Incomplete lessons",
        value: 2600,
        color: "var(--scaffold-incomplete)",
      },
    ],
  },
  teachers: [
    {
      name: "Katie Andorka",
      site: "Greater Dayton School",
      grade: 6,
      lessonsAssigned: 1240,
      sessionsReviewed: 118,
      studentsAssigned: 58,
      activeStudents: 45,
      avgHours: 6.2,
      onTrackPct: 47,
      onTrackDelta: 2,
      avgMastery: 44,
      activity: 84,
      lastActive: "Today",
    },
    {
      name: "Marcus Bell",
      site: "Riverside Middle",
      grade: 8,
      lessonsAssigned: 980,
      sessionsReviewed: 92,
      studentsAssigned: 46,
      activeStudents: 31,
      avgHours: 5.5,
      onTrackPct: 39,
      onTrackDelta: -2,
      avgMastery: 39,
      activity: 66,
      lastActive: "Yesterday",
    },
    {
      name: "Dana Whitfield",
      site: "Riverside Middle",
      grade: 7,
      lessonsAssigned: 760,
      sessionsReviewed: 71,
      studentsAssigned: 37,
      activeStudents: 22,
      avgHours: 4.8,
      onTrackPct: 36,
      onTrackDelta: 1,
      avgMastery: 36,
      activity: 52,
      lastActive: "2d ago",
    },
  ],
  masteryGain: {
    pts: 3,
    sub: "more mastered, this school year",
    trend: [38, 39, 40, 40, 41, 41],
  },
  masteryOverTime: [30, 32, 33, 35, 36, 37, 38, 39, 40, 41, 41],
  standardPerf: [
    { key: "notStarted", value: 20 },
    { key: "approaching", value: 26 },
    { key: "proficient", value: 21 },
    { key: "mastered", value: 14 },
  ],
  studentPerf: [
    { key: "notStarted", value: 64 },
    { key: "approaching", value: 128 },
    { key: "proficient", value: 96 },
    { key: "mastered", value: 54 },
  ],
  topStudents: [
    {
      name: "Sofia G.",
      grade: 6,
      pct: 43,
      score: 3.6,
      bites: 140,
      site: "Greater Dayton School",
      teacher: "Katie Andorka",
    },
    {
      name: "Marcus W.",
      grade: 7,
      pct: 39,
      score: 3.4,
      bites: 150,
      site: "Riverside Middle",
      teacher: "Dana Whitfield",
    },
    {
      name: "Ava D.",
      grade: 8,
      pct: 40,
      score: 3.3,
      bites: 120,
      site: "Riverside Middle",
      teacher: "Marcus Bell",
    },
    {
      name: "Kai N.",
      grade: 6,
      pct: 36,
      score: 3.2,
      bites: 110,
      site: "Greater Dayton School",
      teacher: "Katie Andorka",
    },
    {
      name: "Ruby F.",
      grade: 7,
      pct: 35,
      score: 3.1,
      bites: 130,
      site: "Oakwood Charter",
      teacher: "Linda J.",
    },
    {
      name: "Theo P.",
      grade: 8,
      pct: 34,
      score: 3.0,
      bites: 118,
      site: "Riverside Middle",
      teacher: "Marcus Bell",
    },
    {
      name: "Lena M.",
      grade: 6,
      pct: 33,
      score: 2.9,
      bites: 122,
      site: "Greater Dayton School",
      teacher: "Katie Andorka",
    },
    {
      name: "Omar S.",
      grade: 7,
      pct: 32,
      score: 2.9,
      bites: 115,
      site: "Riverside Middle",
      teacher: "Dana Whitfield",
    },
    {
      name: "Nia B.",
      grade: 8,
      pct: 31,
      score: 2.8,
      bites: 108,
      site: "Riverside Middle",
      teacher: "Marcus Bell",
    },
    {
      name: "Eli T.",
      grade: 7,
      pct: 30,
      score: 2.7,
      bites: 112,
      site: "Oakwood Charter",
      teacher: "Linda J.",
    },
  ],
  bottomStudents: [
    { name: "Jaylen P.", grade: 6, pct: 5, score: 1.2, bites: 48 },
    { name: "Ivy H.", grade: 7, pct: 8, score: 1.4, bites: 52 },
    { name: "Leo M.", grade: 8, pct: 8, score: 1.5, bites: 41 },
    { name: "Nora S.", grade: 6, pct: 10, score: 1.6, bites: 58 },
    { name: "Eli T.", grade: 7, pct: 12, score: 1.7, bites: 61 },
  ],
  actionable: {
    targetPct: 60,
    projectedPct: 44,
    expectedStandards: 14,
    weeksRemaining: 11,
    catchUpSkillsPerWeek: 3,
    offTrackShare: 0.62,
    bubbleShare: 0.16,
    stalledShare: 0.11,
    slippingShare: 0.09,
    bubbleRoster: [
      {
        name: "Jaylen P.",
        grade: 6,
        site: "Greater Dayton School",
        teacher: "Katie Andorka",
        detail: "3 skills from Approaching",
      },
      {
        name: "Ivy H.",
        grade: 7,
        site: "Riverside Middle",
        teacher: "Dana Whitfield",
        detail: "2 skills from Approaching",
      },
      {
        name: "Nora S.",
        grade: 6,
        site: "Greater Dayton School",
        teacher: "Katie Andorka",
        detail: "4 skills from Approaching",
      },
      {
        name: "Leo M.",
        grade: 8,
        site: "Riverside Middle",
        teacher: "Marcus Bell",
        detail: "2 skills from Approaching",
      },
    ],
    stalledRoster: [
      {
        name: "Eli T.",
        grade: 7,
        site: "Riverside Middle",
        teacher: "Dana Whitfield",
        detail: "0 skills in 22 days · 5.2 hrs logged",
      },
      {
        name: "Ivy H.",
        grade: 7,
        site: "Riverside Middle",
        teacher: "Dana Whitfield",
        detail: "0 skills in 17 days · 6.0 hrs logged",
      },
      {
        name: "Marcus W.",
        grade: 8,
        site: "Greater Dayton School",
        teacher: "Marcus Bell",
        detail: "0 skills in 19 days · 5.5 hrs logged",
      },
    ],
    slippingRoster: [
      {
        name: "Nora S.",
        grade: 6,
        site: "Greater Dayton School",
        teacher: "Katie Andorka",
        detail: "On track last month · -9%",
      },
      {
        name: "Leo M.",
        grade: 8,
        site: "Riverside Middle",
        teacher: "Marcus Bell",
        detail: "Fell below pace 2 weeks ago · -7%",
      },
    ],
  },
  schools: [
    {
      name: "Greater Dayton School",
      students: 150,
      medianMastered: 24,
      avgMastery: 44,
      trend: [39, 40, 41, 42, 43, 44],
      mom: 2,
      medianLessons: 10,
    },
    {
      name: "Riverside Middle",
      students: 120,
      medianMastered: 17,
      avgMastery: 37,
      trend: [40, 39, 40, 39, 40, 37],
      mom: -3,
      medianLessons: 8,
    },
    {
      name: "Oakwood Charter",
      students: 72,
      medianMastered: 22,
      avgMastery: 42,
      trend: [37, 38, 40, 41, 41, 42],
      mom: 2,
      medianLessons: 11,
    },
  ],
  allTrend: [38, 39, 40, 40, 41, 41],
  allMom: 2,
  groupsMedianLessons: [
    { name: "Tier 2 Intervention", value: 9 },
    { name: "Tier 3 Intervention", value: 7 },
    { name: "Tier 1 Enrichment", value: 22 },
    { name: "Afterschool Cohort A", value: 13 },
    { name: "Bubble Students", value: 11 },
  ],
};

const ENRICHMENT_VARIANT: Variant = {
  key: "enrichment",
  kpi: {
    standardsMastered: 52,
    standardsMasteredDelta: "+7 this month",
    avgMastery: 79,
    avgMasteryDelta: "+5 pts vs last month",
  },
  histogram: {
    buckets: [4, 10, 22, 38, 54, 40],
    medianIndex: 4,
    medianLessons: 22,
    total: 168,
  },
  grades: [
    {
      grade: 6,
      total: 29,
      notStarted: 1,
      approaching: 3,
      proficient: 6,
      mastered: 19,
    },
    {
      grade: 7,
      total: 28,
      notStarted: 1,
      approaching: 3,
      proficient: 6,
      mastered: 18,
    },
    {
      grade: 8,
      total: 24,
      notStarted: 1,
      approaching: 3,
      proficient: 5,
      mastered: 15,
    },
  ],
  engagement: {
    activePct: 88,
    activeSub: "148 of 168 this period",
    activeMoM: null,
    activeCount: 148,
    standardsPracticed: 74,
    tutoringHours: "1,090 hrs",
    tutoringHoursNum: 1090,
    tutoringSub: "6.5 hrs avg per student",
    tutoringPerStudentNum: 6.5,
    sessions: "14.6",
    sessionsNum: 14.6,
    sessionsSub: "median, this school year",
  },
  lessons: {
    assigned: 3240,
    assignedSub: "across 81 standards",
    completed: 2980,
    completedSub: "92% of assigned lessons",
    completionRate: 92,
    completionMoM: 4,
    tutorHours: "990 hrs",
    tutorHoursNum: 990,
    minPerLesson: 20,
    tutorHoursSub: "20 min avg per lesson",
  },
  scaffolding: {
    total: 7150,
    totalSub: "across 2,980 completed lessons",
    breakdown: [
      {
        label: "Flawless lessons",
        value: 4600,
        color: "var(--scaffold-flawless)",
      },
      { label: "Low scaffolding", value: 1400, color: "var(--scaffold-low)" },
      {
        label: "Medium scaffolding",
        value: 600,
        color: "var(--scaffold-medium)",
      },
      { label: "High scaffolding", value: 300, color: "var(--scaffold-high)" },
      {
        label: "Incomplete lessons",
        value: 250,
        color: "var(--scaffold-incomplete)",
      },
    ],
  },
  teachers: [
    {
      name: "Katie Andorka",
      site: "Greater Dayton School",
      grade: 6,
      lessonsAssigned: 1080,
      sessionsReviewed: 96,
      studentsAssigned: 52,
      activeStudents: 47,
      avgHours: 7.4,
      onTrackPct: 86,
      onTrackDelta: 5,
      avgMastery: 83,
      activity: 95,
      lastActive: "Today",
    },
    {
      name: "Linda J.",
      site: "Oakwood Charter",
      grade: 7,
      lessonsAssigned: 840,
      sessionsReviewed: 74,
      studentsAssigned: 41,
      activeStudents: 35,
      avgHours: 6.9,
      onTrackPct: 80,
      onTrackDelta: 4,
      avgMastery: 79,
      activity: 82,
      lastActive: "Today",
    },
    {
      name: "Dana Whitfield",
      site: "Riverside Middle",
      grade: 7,
      lessonsAssigned: 720,
      sessionsReviewed: 63,
      studentsAssigned: 34,
      activeStudents: 28,
      avgHours: 6.5,
      onTrackPct: 77,
      onTrackDelta: 3,
      avgMastery: 76,
      activity: 71,
      lastActive: "Yesterday",
    },
    {
      name: "Marcus Bell",
      site: "Greater Dayton School",
      grade: 8,
      lessonsAssigned: 600,
      sessionsReviewed: 51,
      studentsAssigned: 28,
      activeStudents: 22,
      avgHours: 6.0,
      onTrackPct: 73,
      onTrackDelta: 2,
      avgMastery: 73,
      activity: 60,
      lastActive: "2d ago",
    },
  ],
  masteryGain: {
    pts: 8,
    sub: "more mastered, this school year",
    trend: [71, 73, 75, 77, 78, 79],
  },
  masteryOverTime: [66, 69, 71, 73, 74, 75, 76, 77, 78, 79, 79],
  standardPerf: [
    { key: "notStarted", value: 3 },
    { key: "approaching", value: 8 },
    { key: "proficient", value: 18 },
    { key: "mastered", value: 52 },
  ],
  studentPerf: [
    { key: "notStarted", value: 6 },
    { key: "approaching", value: 22 },
    { key: "proficient", value: 55 },
    { key: "mastered", value: 85 },
  ],
  topStudents: [
    {
      name: "Hannah V.",
      grade: 6,
      pct: 99,
      score: 4.9,
      bites: 260,
      site: "Greater Dayton School",
      teacher: "Katie Andorka",
    },
    {
      name: "Omar Z.",
      grade: 7,
      pct: 98,
      score: 4.9,
      bites: 250,
      site: "Oakwood Charter",
      teacher: "Linda J.",
    },
    {
      name: "Chloe R.",
      grade: 8,
      pct: 97,
      score: 4.8,
      bites: 230,
      site: "Greater Dayton School",
      teacher: "Marcus Bell",
    },
    {
      name: "Ben K.",
      grade: 6,
      pct: 96,
      score: 4.8,
      bites: 240,
      site: "Greater Dayton School",
      teacher: "Katie Andorka",
    },
    {
      name: "Lucy A.",
      grade: 7,
      pct: 95,
      score: 4.7,
      bites: 220,
      site: "Riverside Middle",
      teacher: "Dana Whitfield",
    },
    {
      name: "Ivan P.",
      grade: 8,
      pct: 94,
      score: 4.7,
      bites: 226,
      site: "Greater Dayton School",
      teacher: "Marcus Bell",
    },
    {
      name: "Maya C.",
      grade: 7,
      pct: 93,
      score: 4.6,
      bites: 214,
      site: "Oakwood Charter",
      teacher: "Linda J.",
    },
    {
      name: "Leo F.",
      grade: 6,
      pct: 92,
      score: 4.6,
      bites: 208,
      site: "St. Anne's Academy",
      teacher: "Dana Whitfield",
    },
    {
      name: "Zara H.",
      grade: 8,
      pct: 91,
      score: 4.5,
      bites: 216,
      site: "Greater Dayton School",
      teacher: "Marcus Bell",
    },
    {
      name: "Noor A.",
      grade: 7,
      pct: 90,
      score: 4.5,
      bites: 202,
      site: "Riverside Middle",
      teacher: "Dana Whitfield",
    },
  ],
  bottomStudents: [
    { name: "Theo J.", grade: 8, pct: 62, score: 3.4, bites: 150 },
    { name: "Mia Q.", grade: 6, pct: 63, score: 3.5, bites: 160 },
    { name: "Sam D.", grade: 7, pct: 64, score: 3.6, bites: 155 },
    { name: "Ella W.", grade: 6, pct: 66, score: 3.7, bites: 170 },
    { name: "Jack B.", grade: 8, pct: 60, score: 3.3, bites: 145 },
  ],
  actionable: {
    targetPct: 90,
    projectedPct: 88,
    expectedStandards: 20,
    weeksRemaining: 11,
    catchUpSkillsPerWeek: 1,
    offTrackShare: 0.12,
    bubbleShare: 0.08,
    stalledShare: 0.03,
    slippingShare: 0.02,
    bubbleRoster: [
      {
        name: "Theo J.",
        grade: 8,
        site: "Riverside Middle",
        teacher: "Marcus Bell",
        detail: "2 skills from Mastered",
      },
      {
        name: "Jack B.",
        grade: 8,
        site: "Greater Dayton School",
        teacher: "Marcus Bell",
        detail: "3 skills from Mastered",
      },
      {
        name: "Mia Q.",
        grade: 6,
        site: "Oakwood Charter",
        teacher: "Linda J.",
        detail: "1 skill from Mastered",
      },
    ],
    stalledRoster: [
      {
        name: "Sam D.",
        grade: 7,
        site: "Riverside Middle",
        teacher: "Dana Whitfield",
        detail: "0 skills in 15 days · 6.4 hrs logged",
      },
    ],
    slippingRoster: [
      {
        name: "Ella W.",
        grade: 6,
        site: "Greater Dayton School",
        teacher: "Katie Andorka",
        detail: "On track last month · -3%",
      },
    ],
  },
  schools: [
    {
      name: "Greater Dayton School",
      students: 60,
      medianMastered: 54,
      avgMastery: 81,
      trend: [72, 74, 76, 78, 80, 81],
      mom: 4,
      medianLessons: 23,
    },
    {
      name: "Riverside Middle",
      students: 44,
      medianMastered: 48,
      avgMastery: 74,
      trend: [73, 74, 75, 74, 75, 74],
      mom: -1,
      medianLessons: 20,
    },
    {
      name: "Oakwood Charter",
      students: 38,
      medianMastered: 56,
      avgMastery: 83,
      trend: [74, 76, 78, 80, 82, 83],
      mom: 6,
      medianLessons: 25,
    },
    {
      name: "St. Anne's Academy",
      students: 26,
      medianMastered: 50,
      avgMastery: 77,
      trend: [70, 72, 73, 75, 76, 77],
      mom: 3,
      medianLessons: 21,
    },
  ],
  allTrend: [71, 73, 75, 77, 78, 79],
  allMom: 5,
  groupsMedianLessons: [
    { name: "Tier 2 Intervention", value: 9 },
    { name: "Tier 3 Intervention", value: 7 },
    { name: "Tier 1 Enrichment", value: 22 },
    { name: "Afterschool Cohort A", value: 13 },
    { name: "Bubble Students", value: 11 },
  ],
};

export const VARIANTS: Record<string, Variant> = {
  default: DEFAULT_VARIANT,
  tier2: TIER2_VARIANT,
  enrichment: ENRICHMENT_VARIANT,
};

// Group label -> variant key. Only single-group selections switch the variant.
export function variantForGroups(groups: string[]): string {
  if (groups.length === 1) {
    if (
      groups[0] === "Tier 2 Intervention" ||
      groups[0] === "Tier 3 Intervention"
    )
      return "tier2";
    if (groups[0] === "Tier 1 Enrichment") return "enrichment";
  }
  return "default";
}

// ---- Reactivity engine -----------------------------------------------------
// Every metric is derived from a base variant, scaled by the active filters
// (schools, teachers, grades, and time period) so the dashboard stays reactive.

// throughput = cumulative counts (lessons, hours, events, sessions)
// reach = coverage rates (active %, standards practiced)
export const PERIOD_THROUGHPUT: Record<string, number> = {
  "This week": 0.05,
  "This month": 0.18,
  "This semester": 0.55,
  "This year": 1,
};
export const PERIOD_REACH: Record<string, number> = {
  "This week": 0.55,
  "This month": 0.74,
  "This semester": 0.92,
  "This year": 1,
};
export const PERIOD_PHRASE: Record<string, string> = {
  "This week": "this week",
  "This month": "this month",
  "This semester": "this semester",
  "This year": "this school year",
};

// Rough student weight per grade, used to react to the grade chips.
const GRADE_WEIGHT: Record<number, number> = { 6: 0.37, 7: 0.34, 8: 0.29 };
export function gradeFraction(grades: number[]): number {
  const all = GRADES.reduce((s, g) => s + (GRADE_WEIGHT[g] || 0), 0);
  const sel = grades.reduce((s, g) => s + (GRADE_WEIGHT[g] || 0), 0);
  return all > 0 ? sel / all : 0;
}

export const SCAFFOLD_BITES = [
  { label: "Flawless lessons", color: "var(--scaffold-flawless)" },
  { label: "Low scaffolding", color: "var(--scaffold-low)" },
  { label: "Medium scaffolding", color: "var(--scaffold-medium)" },
  { label: "High scaffolding", color: "var(--scaffold-high)" },
] as const;

export interface DeriveCtx {
  popMult: number; // population share of the base variant (schools x grades x teachers)
  throughputMult: number; // popMult x period throughput
  reachFactor: number; // period reach factor
  teacherRowMult: number; // per-teacher scaling (period x schools x grades, no teacher count)
  studentsDisplayed: number;
  period: string;
  selectedTeachers: string[];
  selectedSites: string[];
}

const fmtN = (n: number) => Math.round(n).toLocaleString("en-US");
const scale = (n: number, m: number) => Math.max(0, Math.round(n * m));

// Split a count across the selected grades (for one-level-deeper hover reveals).
export function gradeSplit(
  total: number,
  grades: number[],
): { label: string; value: string }[] {
  const gs = grades.length ? grades : [...GRADES];
  const totalW = gs.reduce((s, g) => s + (GRADE_WEIGHT[g] || 0), 0);
  return gs.map((g) => {
    const share = totalW > 0 ? (GRADE_WEIGHT[g] || 0) / totalW : 0;
    return { label: `Grade ${g}`, value: fmtN(total * share) };
  });
}

// Split a count across the visible schools (for one-level-deeper hover reveals).
export function schoolSplit(
  total: number,
  schools: { name: string; students: number }[],
): { label: string; value: string }[] {
  const totalStudents = schools.reduce((s, r) => s + r.students, 0);
  return schools.map((r) => {
    const share = totalStudents > 0 ? r.students / totalStudents : 0;
    return { label: r.name, value: fmtN(total * share) };
  });
}

// Rate / median metrics: show a believable per-group value with slight variance.
const GRADE_RATE_MULT: Record<number, number> = { 6: 1.04, 7: 1.0, 8: 0.94 };
const SCHOOL_RATE_MULT = [1.06, 0.96, 1.02, 0.92, 1.0];

export function gradeRateSplit(
  base: number,
  grades: number[],
  format: (n: number) => string,
): { label: string; value: string }[] {
  const gs = grades.length ? grades : [...GRADES];
  return gs.map((g) => ({
    label: `Grade ${g}`,
    value: format(base * (GRADE_RATE_MULT[g] ?? 1)),
  }));
}

export function schoolRateSplit(
  base: number,
  schools: { name: string; students: number }[],
  format: (n: number) => string,
): { label: string; value: string }[] {
  return schools.map((r, i) => ({
    label: r.name,
    value: format(base * (SCHOOL_RATE_MULT[i % SCHOOL_RATE_MULT.length] ?? 1)),
  }));
}

export function deriveVariant(base: Variant, ctx: DeriveCtx): Variant {
  const {
    popMult,
    throughputMult,
    reachFactor,
    teacherRowMult,
    studentsDisplayed,
    period,
    selectedTeachers,
    selectedSites,
  } = ctx;
  const phrase = PERIOD_PHRASE[period] || "this school year";
  const tp = PERIOD_THROUGHPUT[period] ?? 1;

  // lessons
  const assigned = scale(base.lessons.assigned, throughputMult);
  const completed = scale(base.lessons.completed, throughputMult);
  const completionRate =
    assigned > 0 ? Math.round((completed / assigned) * 100) : 0;
  const tutorHoursNum = scale(base.lessons.tutorHoursNum, throughputMult);

  // engagement
  const activeCount = Math.round(
    base.engagement.activeCount * popMult * reachFactor,
  );
  const activePct =
    studentsDisplayed > 0
      ? Math.min(100, Math.round((activeCount / studentsDisplayed) * 100))
      : 0;
  const standardsPracticed = Math.min(
    81,
    Math.round(base.engagement.standardsPracticed * reachFactor),
  );
  const tutoringHoursNum = scale(
    base.engagement.tutoringHoursNum,
    throughputMult,
  );
  const tutoringPerStudentNum = +(
    base.engagement.tutoringPerStudentNum * tp
  ).toFixed(1);
  const sessionsNum = +(base.engagement.sessionsNum * tp).toFixed(1);

  // scaffolding
  const scTotal = scale(base.scaffolding.total, throughputMult);
  const breakdown = base.scaffolding.breakdown.map((b) => ({
    ...b,
    value: scale(b.value, throughputMult),
  }));

  // teachers
  let teachers = base.teachers;
  if (selectedSites.length)
    teachers = teachers.filter((t) => selectedSites.includes(t.site));
  if (selectedTeachers.length)
    teachers = teachers.filter((t) => selectedTeachers.includes(t.name));
  teachers = teachers.map((t) => ({
    ...t,
    lessonsAssigned: scale(t.lessonsAssigned, teacherRowMult),
    sessionsReviewed: scale(t.sessionsReviewed, teacherRowMult),
  }));

  // performance
  const gainPts = Math.max(1, Math.round(base.masteryGain.pts * tp));
  const studentPerf = base.studentPerf.map((d) => ({
    ...d,
    value: scale(d.value, popMult),
  }));

  // histogram (population distribution: reacts to school/grade/teacher, not period)
  const histogram = {
    ...base.histogram,
    buckets: base.histogram.buckets.map((v) => scale(v, popMult)),
    total: scale(base.histogram.total, popMult),
  };

  return {
    ...base,
    histogram,
    engagement: {
      ...base.engagement,
      activePct,
      activeCount,
      activeSub: `${fmtN(activeCount)} of ${fmtN(studentsDisplayed)} this period`,
      standardsPracticed,
      tutoringHoursNum,
      tutoringHours: `${fmtN(tutoringHoursNum)} hrs`,
      tutoringPerStudentNum,
      tutoringSub: `${tutoringPerStudentNum} hrs avg per student`,
      sessionsNum,
      sessions: `${sessionsNum}`,
      sessionsSub: "median, this school year",
    },
    lessons: {
      ...base.lessons,
      assigned,
      completed,
      completionRate,
      completedSub: `${completionRate}% of assigned lessons`,
      tutorHoursNum,
      tutorHours: `${fmtN(tutorHoursNum)} hrs`,
      tutorHoursSub: `${base.lessons.minPerLesson} min avg per lesson`,
    },
    scaffolding: {
      ...base.scaffolding,
      total: scTotal,
      totalSub: `across ${fmtN(completed)} completed lessons`,
      breakdown,
    },
    teachers,
    masteryGain: {
      ...base.masteryGain,
      pts: gainPts,
      sub: `more mastered, ${phrase}`,
    },
    studentPerf,
  };
}

// Deterministic scaffolding distribution for the comparison view, keyed on a
// 0-100 quality score (higher quality -> more flawless, less high scaffolding).
export function scaffoldDistribution(quality: number): {
  flawless: number;
  low: number;
  medium: number;
  high: number;
} {
  const q = Math.max(0, Math.min(100, quality));
  const flawless = Math.round(q * 0.66);
  const high = Math.round((100 - q) * 0.32);
  const rest = Math.max(0, 100 - flawless - high);
  const low = Math.round(rest * 0.62);
  const medium = rest - low;
  return { flawless, low, medium, high };
}

// Exact allocation of grade mastery levels across domains.
// Guarantees: each domain row sums to its structural total, and each
// mastery-level column sums to the grade-stack level total.
export function allocateDomains(
  grade: number,
  stack: {
    notStarted: number;
    approaching: number;
    proficient: number;
    mastered: number;
  },
) {
  const totals = DOMAIN_TOTALS[grade];
  const domains = ["RP", "NS", "EE", "G", "SP"] as const;
  const remaining: Record<string, number> = {};
  domains.forEach((d) => (remaining[d] = totals[d]));

  const result: Record<string, Record<MasteryLevel, number>> = {};
  domains.forEach(
    (d) =>
      (result[d] = {
        notStarted: 0,
        approaching: 0,
        proficient: 0,
        mastered: 0,
      }),
  );

  const order: MasteryLevel[] = [
    "mastered",
    "proficient",
    "approaching",
    "notStarted",
  ];

  order.forEach((lvl, li) => {
    let count = stack[lvl];
    if (li === order.length - 1) {
      // last level: fill each domain's remaining capacity exactly
      domains.forEach((d) => {
        result[d][lvl] = remaining[d];
        remaining[d] = 0;
      });
      return;
    }
    const capacityTotal = domains.reduce((s, d) => s + remaining[d], 0);
    // ideal proportional shares, then largest-remainder rounding, capped by capacity
    const ideal = domains.map((d) =>
      capacityTotal > 0 ? (count * remaining[d]) / capacityTotal : 0,
    );
    const base = domains.map((_, i) =>
      Math.min(Math.floor(ideal[i]), remaining[domains[i]]),
    );
    let assigned = base.reduce((s, n) => s + n, 0);
    const rema = domains
      .map((d, i) => ({
        i,
        d,
        frac: ideal[i] - base[i],
        cap: remaining[d] - base[i],
      }))
      .sort((a, b) => b.frac - a.frac);
    let k = 0;
    while (assigned < count && k < rema.length * 4) {
      const slot = rema[k % rema.length];
      if (slot.cap > 0) {
        base[slot.i] += 1;
        slot.cap -= 1;
        assigned += 1;
      }
      k++;
    }
    domains.forEach((d, i) => {
      result[d][lvl] = base[i];
      remaining[d] -= base[i];
    });
  });

  return domains.map((d) => ({
    domain: d,
    total: totals[d],
    ...result[d],
    masteredLabel: `${result[d].mastered} / ${totals[d]}`,
  }));
}
